import { useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { NavigationGuardContext } from './navigationGuardContext';
import type { NavigationGuard } from './navigationGuardContext';

/**
 * Lets a page stop an in-app navigation while it has unsaved work.
 *
 * React Router 6 ships `useBlocker`, but only for data routers created with
 * `createBrowserRouter`. This app renders a plain `<BrowserRouter>`, so calling
 * it throws — converting the whole route tree to a data router to guard one
 * form would be a large change with a large blast radius.
 *
 * Intercepting link clicks would not work either: the sidebar rows are
 * `ListItemButton`s with `onClick` handlers, not anchors, so there is no `<a>`
 * to catch.
 *
 * So navigation away asks first. The two places that navigate out of a page
 * without the page's knowledge — the sidebar and the user menu — route through
 * `attemptNavigate`, and a guarded page gets the chance to defer.
 *
 * The hook and context live in `navigationGuardContext.ts`; this file exports
 * only the component, which is what the lint gate requires.
 */
export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  // A ref, not state: registering a guard must not re-render the whole shell,
  // and the guard is read at click time rather than at render time.
  const guardRef = useRef<NavigationGuard | null>(null);

  const registerGuard = useCallback((guard: NavigationGuard | null) => {
    guardRef.current = guard;
  }, []);

  const attemptNavigate = useCallback((proceed: () => void) => {
    const guard = guardRef.current;
    if (guard === null) {
      proceed();
      return;
    }
    // The guard reports whether it let the navigation through. If it did not,
    // it owns `proceed` now.
    guard(proceed);
  }, []);

  const value = useMemo(
    () => ({ registerGuard, attemptNavigate }),
    [registerGuard, attemptNavigate],
  );

  return (
    <NavigationGuardContext.Provider value={value}>{children}</NavigationGuardContext.Provider>
  );
}
